import { useMemo, useState, useRef, useEffect, useCallback, type ChangeEvent } from 'react'
import type { AnswerValue, WorkflowStage } from '../../types/workflow'
import { API_BASE_URL } from '@/lib/apiConfig';
interface IdUploadTemplateProps {
  stage: WorkflowStage
  patientId?: string
  existingKey?: string | null
  onSelect: (value: AnswerValue) => void
}

function createUuid() {
  return crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

export default function IdUploadTemplate({ stage, patientId, existingKey, onSelect }: IdUploadTemplateProps) {
  const [isUploading, setIsUploading] = useState(false)
  const [uploadedKey, setUploadedKey] = useState<string | null>(null)
  const [uploadedImage, setUploadedImage] = useState<string | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [isCameraOpen, setIsCameraOpen] = useState(false)
  const [isFrontCamera, setIsFrontCamera] = useState(false)
  const [stream, setStream] = useState<MediaStream | null>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const resolvedPatientId = useMemo(() => patientId || 'unknown-patient', [patientId])
  const fileInputRef = useRef<HTMLInputElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)

  const startCamera = useCallback(async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: isFrontCamera ? 'user' : 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      })
      setStream(mediaStream)
      setIsCameraOpen(true)
    } catch {
      setUploadError('Camera access is required. Please allow camera permissions or upload a file instead.')
    }
  }, [isFrontCamera])

  useEffect(() => {
    if (stream && videoRef.current) {
      videoRef.current.srcObject = stream
    }
  }, [stream])

  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop())
      setStream(null)
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null
    }
    setIsCameraOpen(false)
  }, [stream])

  useEffect(() => {
    if (!existingKey) return

    const loadExistingPreview = async () => {
      try {
        const apiUrl = API_BASE_URL
        const response = await fetch(`${apiUrl}/uploads/view-url?key=${encodeURIComponent(existingKey)}`)
        const json = await response.json()
        if (json?.success && json?.data?.viewUrl) {
          setUploadedImage(json.data.viewUrl)
        } else {
          setUploadedImage(null)
        }
      } catch {
        setUploadedImage(null)
      } finally {
        setUploadedKey(existingKey)
        setUploadError(null)
      }
    }

    void loadExistingPreview()
  }, [existingKey])

  const capturePhoto = async () => {
    if (!videoRef.current || !canvasRef.current) return
    const video = videoRef.current
    const canvas = canvasRef.current
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.drawImage(video, 0, 0)

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((capturedBlob) => resolve(capturedBlob), 'image/jpeg', 0.85)
    })
    if (!blob) return

    const file = new File([blob], 'id-photo.jpg', { type: 'image/jpeg' })
    setSelectedFile(file)
    setUploadedImage(URL.createObjectURL(blob))
    setUploadedKey(null)
    setUploadError(null)
    stopCamera()
  }

  const handleFileSelect = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    if (file.size > 10 * 1024 * 1024) {
      setUploadError('File too large. Please upload an image under 10MB.')
      return
    }
    setSelectedFile(file)
    setUploadedImage(URL.createObjectURL(file))
    setUploadedKey(null)
    setUploadError(null)
  }

  const uploadFile = async (file: File) => {
    setIsUploading(true)
    setUploadError(null)
    try {
      const apiUrl = API_BASE_URL
      const filename = `${resolvedPatientId}-id-photo-${Date.now()}-${createUuid()}.jpg`
      const presignRes = await fetch(`${apiUrl}/uploads/public/document`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filename,
          contentType: file.type || 'image/jpeg',
          documentType: 'glp1_id_photo',
        }),
      })
      const presignJson = await presignRes.json()
      if (!presignJson?.success) throw new Error('Failed to get upload URL')
      const uploadResponse = await fetch(presignJson.data.uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type || 'image/jpeg',
          // Presigned URL from /uploads/public/document signs this header.
          'x-amz-server-side-encryption': 'AES256',
        },
        body: file,
      })
      if (!uploadResponse.ok) {
        throw new Error(`S3 upload failed (${uploadResponse.status})`)
      }
      if (!presignJson?.data?.key) {
        throw new Error('Upload succeeded but no file key was returned')
      }
      return presignJson.data.key
    } finally {
      setIsUploading(false)
    }
  }

  const handleContinue = async () => {
    if (uploadedKey) {
      onSelect({ id_photo_key: uploadedKey })
      return
    }
    if (!selectedFile) {
      setUploadError('Please upload or capture your ID photo first.')
      return
    }
    try {
      const key = await uploadFile(selectedFile)
      setUploadedKey(key)
      onSelect({ id_photo_key: key })
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : 'Upload failed. Please try again.')
    }
  }

  const handleRetake = () => {
    setSelectedFile(null)
    setUploadedImage(null)
    setUploadedKey(null)
    setUploadError(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop())
      }
    }
  }, [stream])

  return (
    <div className="flex-1 flex flex-col gap-4">
      {isCameraOpen ? (
        <div className="flex flex-col gap-3">
          <div className="relative aspect-[4/3] overflow-hidden rounded-xl bg-black">
            <video ref={videoRef} autoPlay playsInline muted className="h-full w-full object-cover" />
          </div>
          <div className="flex gap-2">
            <button
              className="cursor-pointer rounded-xl border border-white/20 bg-white/[0.03] px-4 py-3 text-[14px] font-medium text-white/80 hover:bg-white/[0.06]"
              onClick={stopCamera}
            >
              Cancel
            </button>
            <button
              className="flex-1 cursor-pointer rounded-xl border border-white/20 bg-white/[0.03] px-4 py-3 text-[14px] font-medium text-white hover:bg-white/[0.06]"
              onClick={() => {
                setIsFrontCamera(!isFrontCamera)
                stopCamera()
                setTimeout(() => {
                  void startCamera()
                }, 100)
              }}
            >
              Flip Camera
            </button>
            <button
              className="flex-1 cursor-pointer rounded-xl border-none bg-[var(--ww-btn-bg)] px-4 py-3 text-[14px] font-semibold text-[var(--ww-btn-text)]"
              onClick={() => {
                void capturePhoto()
              }}
            >
              Take Photo
            </button>
          </div>
          <canvas ref={canvasRef} className="hidden" />
        </div>
      ) : uploadedImage || uploadedKey ? (
        <div className="flex flex-col gap-3">
          {uploadedImage ? (
            <div className="overflow-hidden rounded-xl border border-white/20">
              <img src={uploadedImage} alt="ID preview" className="block h-auto w-full" />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-white/20 bg-white/[0.03] p-8">
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-emerald-300"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <div className="text-center">
                <p className="mb-1 text-[15px] font-medium text-white">ID uploaded</p>
                <p className="text-[13px] text-white/60">Your ID photo was successfully uploaded</p>
              </div>
            </div>
          )}
          {uploadError && <div className="text-center text-[13px] text-red-400">{uploadError}</div>}
          <button
            className="self-center cursor-pointer border-none bg-transparent text-[13px] text-[var(--ww-accent)] hover:underline"
            onClick={handleRetake}
            disabled={isUploading}
          >
            Retake / Re-upload
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <p className="m-0 text-[13px] leading-relaxed text-white/60">
            Please upload a photo of your government-issued ID. It needs to clearly show your photo, name, and date of birth.
          </p>

          <div className="grid grid-cols-2 gap-2">
            <button
              className="flex cursor-pointer flex-col items-center gap-2 rounded-xl border border-white/20 bg-white/[0.03] px-4 py-6 transition-all hover:bg-white/[0.06]"
              onClick={() => {
                void startCamera()
              }}
            >
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-[var(--ww-accent)]">
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                <circle cx="12" cy="13" r="4" />
              </svg>
              <span className="text-[13px] font-medium text-white">Take Photo</span>
            </button>

            <button
              className="flex cursor-pointer flex-col items-center gap-2 rounded-xl border border-white/20 bg-white/[0.03] px-4 py-6 transition-all hover:bg-white/[0.06]"
              onClick={() => fileInputRef.current?.click()}
            >
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-[var(--ww-accent)]">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              <span className="text-[13px] font-medium text-white">Upload File</span>
            </button>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={handleFileSelect}
          />
          {uploadError && <div className="text-center text-[13px] text-red-400">{uploadError}</div>}
        </div>
      )}

      <div className="mt-auto pt-6 pb-2 -ml-[42px] w-[calc(100%+42px)]">
        <button
          className="w-full py-4 px-6 text-[15px] font-semibold text-[var(--ww-btn-text)] bg-[var(--ww-btn-bg)] border-none rounded-full cursor-pointer transition-all duration-300 shadow-[0_2px_8px_rgba(0,0,0,0.1)] hover:not-disabled:bg-[var(--ww-btn-hover)] hover:not-disabled:-translate-y-0.5 disabled:opacity-25 disabled:cursor-default"
          disabled={(!selectedFile && !uploadedKey) || isUploading}
          onClick={() => {
            void handleContinue()
          }}
        >
          {isUploading ? 'Uploading...' : 'Continue'}
        </button>
      </div>
    </div>
  )
}
